import React from 'react';
import { Link } from 'react-router-dom';
import { Prompt } from '../types';
import { StarIcon, HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { HeartIcon } from '@heroicons/react/24/outline';

interface PromptCardGridProps {
  prompt: Prompt;
  isFavorited: boolean;
  onFavorite: (id: string) => void;
  delay?: number;
}

const toolBadgeClass = (tool: string): string => {
  const t = tool.toLowerCase();
  if (t.includes('chatgpt') || t.includes('claude') || t.includes('gemini')) return 'chip chip-purple';
  if (t.includes('dall') || t.includes('midjourney') || t.includes('stable')) return 'chip chip-pink';
  if (t.includes('github') || t.includes('cursor') || t.includes('tabnine')) return 'chip chip-green';
  return 'chip chip-signal';
};

export const PromptCardGrid: React.FC<PromptCardGridProps> = ({ prompt, isFavorited, onFavorite, delay = 0 }) => {
  const author = typeof prompt.author === 'object' ? prompt.author : null;
  const snippet = prompt.prompt?.slice(0, 380) || prompt.description?.slice(0, 380) || '';
  const [hovered, setHovered] = React.useState(false);
  const hasImage = prompt.images && prompt.images.length > 0;

  return (
    <div
      className="card animate-fade-up flex flex-col h-full"
      style={{
        padding: 0,
        animationDelay: `${delay}ms`,
        borderLeft: '3px solid transparent',
        transition: 'border-left-color 200ms ease, box-shadow 220ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1), border-color 220ms ease',
        overflow: 'hidden',
        ...(hovered ? { borderLeftColor: 'var(--signal)' } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image preview if available */}
      {hasImage && (
        <div style={{
          width: '100%',
          height: 180,
          backgroundColor: 'var(--surface-2)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          <img
            src={prompt.images![0]}
            alt={prompt.title}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 300ms ease',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
            }}
          />
        </div>
      )}
      <div style={{ padding: '14px 18px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Top row: Tool badge + NSFW + favorite */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={toolBadgeClass(prompt.aiTool)}>{prompt.aiTool}</span>
            {prompt.isNSFW && (
              <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#ef4444', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 3, padding: '1px 5px', letterSpacing: '0.06em' }}>NSFW</span>
            )}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); onFavorite(prompt._id); }}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 6,
              color: isFavorited ? 'var(--destructive)' : 'var(--text-secondary)',
              opacity: isFavorited ? 1 : 0.45,
              transition: 'opacity 150ms, color 150ms, background 150ms',
              minWidth: 28, minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--destructive)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.08)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = isFavorited ? '1' : '0.45'; (e.currentTarget as HTMLButtonElement).style.color = isFavorited ? 'var(--destructive)' : 'var(--text-secondary)'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
          >
            {isFavorited ? <HeartSolid style={{ width: 14, height: 14 }} /> : <HeartIcon style={{ width: 14, height: 14 }} />}
          </button>
        </div>

        {/* Category chip */}
        <div>
          <span className="chip" style={{ fontSize: 10 }}>{prompt.category.replace(/-/g, ' ')}</span>
        </div>

        {/* Title */}
        <Link to={`/prompts/${prompt._id}`}>
          <h3
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 14,
              fontWeight: 700,
              color: hovered ? 'var(--signal)' : 'var(--text-primary)',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              margin: 0,
              transition: 'color 150ms',
            }}
            className="line-clamp-1"
          >
            {prompt.title}
          </h3>
        </Link>

        {snippet && (
          <div style={{
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            flex: 1,
            marginBottom: 0,
          }}>
            {snippet}{snippet.length >= 380 && <span style={{ opacity: 0.4 }}>…</span>}
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1" title={`Rating: ${prompt.ratings.average.toFixed(1)}`}>
            <StarIcon style={{ width: 12, height: 12, color: 'var(--signal)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{prompt.ratings.average.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.5, fontFamily: 'JetBrains Mono, monospace' }}>{prompt.views.toLocaleString()}</span>
          </div>
        </div>
        {author && (
          <Link to={`/profile/${author.username}`} className="flex items-center gap-1.5" style={{ transition: 'opacity 150ms' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.8')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
          >
            <img
              src={author.avatar || `https://ui-avatars.com/api/?name=${author.username}&background=E8EDF8&color=3D4F6B&size=32`}
              alt={author.username}
              className="avatar"
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 10, color: 'var(--text-secondary)', opacity: 0.65, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{author.username}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default PromptCardGrid;
